import { graphql, useStaticQuery } from "gatsby";
import { getStoryblokLanguage } from "../../config/storyblok-config";
import { useAppState } from "../context/app/context";

interface StoryblokProuctVariantImage {
	filename: string;
	alt?: string;
}

interface StoryblokProductVariant {
	path: string;
	images?: StoryblokProuctVariantImage[];
}

export type StoryblokProductVariants = Record<string, StoryblokProductVariant>;

export const useStoryblokProductVariants = (
	skus: string[],
): Partial<StoryblokProductVariants> => {
	const { siteConfig } = useAppState();
	const currentLanguage = getStoryblokLanguage();

	// Query Storyblok entries with direct access to SKU
	const { allStoryblokEntry } =
		useStaticQuery<Queries.StoryblokProductVariantQuery>(graphql`
			query StoryblokProductVariant {
				allStoryblokEntry(
					filter: { field_component: { eq: "product_variant" } }
				) {
					nodes {
						lang
						full_slug
						commercetools_variant {
							sku
						}
						product_variant_images {
							stores
							image {
								filename
								alt
							}
						}
					}
				}
			}
		`);

	// Build a map of SKU to variant data
	const variantMap: StoryblokProductVariants = {};

	const variants = allStoryblokEntry.nodes.filter(
		(variant) => variant.lang === currentLanguage,
	);

	// Process only variants in the current language
	for (const variant of variants) {
		const sku = variant.commercetools_variant?.sku;
		const path = variant.full_slug;

		// Skip variants without SKU or path
		if (!sku || !path) continue;

		// Filter images for the current store
		const images: StoryblokProuctVariantImage[] = [];
		if (variant.product_variant_images) {
			for (const img of variant.product_variant_images) {
				if (img?.stores?.includes(siteConfig.store) && img.image?.filename) {
					images.push({
						filename: img.image.filename,
						alt: img.image.alt || "",
					});
				}
			}
		}

		variantMap[sku] = { path, images };
	}

	// Return only the requested SKUs
	return skus.reduce((result, sku) => {
		if (variantMap[sku]) {
			result[sku] = variantMap[sku];
		}
		return result;
	}, {} as StoryblokProductVariants);
};
